// §5.3 Landing page (/welcome). No login form, no net-banking register link.
import { useSearchParams, useNavigate } from 'react-router';
import { useState } from 'react';
import { setSession } from '@/api/session';
import { Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldHalved,
  faWallet,
  faClock,
} from '@fortawesome/free-solid-svg-icons';
import { AUTH_LABELS } from '@/constants/static/loginLabels';
import skyline from '../../assets/images/skyline.svg';
import '../Register/Register.css';

const Welcome = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [reference, setReference] = useState('');
  const submitted = params.get('submitted') === '1';
  const beginTracking = () => {
    const value = reference.trim();
    if (!value) return;
    setSession({ type: 'TRACKING', reference: value });
    navigate('/applicant');
  };

  return (
    <div className="nova-register-page">
      <div className="nova-register-container">
        {/* Left Hero Section — same panel as Login / OTP */}
        <div className="nova-hero-section">
          <div className="nova-hero-content">
            <div className="nova-logo">
              <div className="nova-logo-mark">{AUTH_LABELS.app.logoMark}</div>
              <span className="nova-logo-text">{AUTH_LABELS.app.name}</span>
            </div>

            <h1 className="nova-hero-title">
              {AUTH_LABELS.hero.titleLine1}
              <br />
              {AUTH_LABELS.hero.titleLine2}
            </h1>

            <div className="nova-feature-cards">
              <div className="nova-feature-card">
                <div className="nova-feature-icon">
                  <FontAwesomeIcon icon={faShieldHalved} />
                </div>
                <div className="nova-feature-body">
                  <h3 className="nova-feature-title">{AUTH_LABELS.hero.featureSecureTitle}</h3>
                  <p className="nova-feature-description">
                    {AUTH_LABELS.hero.featureSecureDescription}
                  </p>
                </div>
              </div>

              <div className="nova-feature-card">
                <div className="nova-feature-icon">
                  <FontAwesomeIcon icon={faWallet} />
                </div>
                <div className="nova-feature-body">
                  <h3 className="nova-feature-title">{AUTH_LABELS.hero.featureManageTitle}</h3>
                  <p className="nova-feature-description">
                    {AUTH_LABELS.hero.featureManageDescription}
                  </p>
                </div>
              </div>

              <div className="nova-feature-card">
                <div className="nova-feature-icon">
                  <FontAwesomeIcon icon={faClock} />
                </div>
                <div className="nova-feature-body">
                  <h3 className="nova-feature-title">{AUTH_LABELS.hero.featureBankingTitle}</h3>
                  <p className="nova-feature-description">
                    {AUTH_LABELS.hero.featureBankingDescription}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <img src={skyline} alt={AUTH_LABELS.hero.skylineAlt} className="nova-skyline" />
        </div>

        {/* Right Section — existing welcome content on a white card */}
        <div className="nova-form-section">
          <div style={{ width: '100%', maxWidth: 600 }}>
            {submitted && (
              <div className="alert alert-success mb-3" role="status">
                Application submitted. Use your application reference below to track it.
              </div>
            )}

            <div className="nova-form-card" style={{ textAlign: 'center' }}>
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  background: 'var(--nova-primary)',
                  color: '#ffffff',
                  fontSize: '1.75rem',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                }}
              >
                {AUTH_LABELS.app.logoMark}
              </div>

              <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#122b4f', marginBottom: 8 }}>
                {AUTH_LABELS.app.name}
              </h1>
              <p style={{ color: '#5c6b80', marginBottom: 32 }}>
                Your trusted personal banking app
              </p>

              <button
                type="button"
                onClick={() => navigate('/open-account')}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  padding: '16px 20px',
                  background: '#ffffff',
                  border: '1px solid #dbe4f0',
                  borderRadius: 12,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 10,
                    background: 'var(--nova-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem',
                    flexShrink: 0,
                  }}
                >
                  🏦
                </span>
                <span style={{ fontWeight: 600, color: '#122b4f' }}>
                  New to NovaBank? Get your account today in 10 minutes.
                </span>
              </button>

              <div className="mt-4 text-start">
                <label htmlFor="application-reference" className="form-label">Track my application</label>
                <div className="input-group">
                  <input id="application-reference" className="form-control" placeholder="Application reference" value={reference} onChange={(event) => setReference(event.target.value)} />
                  <button type="button" className="btn btn-outline-primary" disabled={!reference.trim()} onClick={beginTracking}>Track</button>
                </div>
                <small className="text-muted">Tracking does not require login before approval.</small>
              </div>

              <p style={{ color: '#5c6b80', margin: '24px 0 12px' }}>
                Already have an account?
              </p>
              <button
                type="button"
                onClick={() => navigate('/login')}
                style={{
                  width: '100%',
                  padding: '14px 0',
                  background: 'var(--nova-primary)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: 10,
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Login
              </button>

              <footer style={{ marginTop: 32, fontSize: '0.85rem', color: '#5c6b80' }}>
                At NovaBank, being on your side includes protecting your personal data.
                <br />
                <button
                  type="button"
                  onClick={() => setShowPrivacy(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    color: 'var(--nova-primary)',
                    textDecoration: 'underline',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                  }}
                >
                  Read our Privacy Notice
                </button>
              </footer>
            </div>
          </div>
        </div>
      </div>

      <Modal show={showPrivacy} onHide={() => setShowPrivacy(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Privacy Notice</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          The full Privacy Notice will be published here before the app goes live.
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Welcome;
